'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import { Link as IntlLink } from '@/i18n/navigation';

import { usePrefersReducedMotion } from './usePrefersReducedMotion';

const MONO = "'JetBrains Mono', monospace";
// Theme tokens — the block flips with the theme (white in light, dark in dark).
const FG = 'var(--color-fg)';
const BG = 'var(--color-bg)';
const MUTED = 'var(--color-muted)';
const ACCENT = 'var(--color-accent)';
const BORDER = 'var(--color-border)';
const BORDERH = 'var(--color-border-hover)';
const SURFACE = 'var(--color-surface)';
const SUBTLE = 'var(--color-border-subtle)';

type Sev = 'high' | 'med';
interface VectorMeta {
	num: string;
	tag: string;
	id: string;
	score: number;
	/** Mono signal tags (language-invariant) + severity, parallel to i18n labels. */
	sigTags: string[];
	sev: Sev[];
}

// Structural (non-translated): HUD codes, tags, ids, scores, severities, chips.
const VECTORS: readonly VectorMeta[] = [
	{
		num: '01',
		tag: 'DEVICE_FP',
		id: '#4471',
		score: 96,
		sigTags: ['EMULATOR', 'DC_IP', 'INPUT', 'REPLAY'],
		sev: ['high', 'high', 'med', 'high'],
	},
	{
		num: '02',
		tag: 'LINK_GRAPH',
		id: '#4472',
		score: 92,
		sigTags: ['DEVICE', 'DOC', 'RING', 'SUBNET'],
		sev: ['high', 'high', 'high', 'med'],
	},
	{
		num: '03',
		tag: 'BEHAVIOR',
		id: '#4473',
		score: 74,
		sigTags: ['SWAP', 'GEO', 'BEHAV', 'NEW_FP'],
		sev: ['high', 'med', 'med', 'med'],
	},
	{
		num: '04',
		tag: 'NETWORK',
		id: '#4474',
		score: 99,
		sigTags: ['NET_ID', 'BANS', 'FRAUD_DOC', 'BIO'],
		sev: ['high', 'high', 'high', 'high'],
	},
];
const CHIPS = [
	'DEVICE_FP',
	'BEHAVIOR',
	'LINK_GRAPH',
	'VELOCITY',
	'NETWORK',
	'24/7 MONITORING',
];

interface VectorCopy {
	name: string;
	signals: string[];
}

const STEP = 330;

export function AntifraudModuleSection() {
	const t = useTranslations('AntifraudModule');
	const reducedMotion = usePrefersReducedMotion();
	const vectors = t.raw('vectors') as VectorCopy[];

	const [active, setActive] = useState(0);
	const [run, setRun] = useState(0);
	const [revealed, setRevealed] = useState(0);
	const [score, setScore] = useState(0);
	const [done, setDone] = useState(false);

	const v = VECTORS[active];
	const n = v.sigTags.length;
	const displayRevealed = reducedMotion ? n : revealed;
	const displayScore = reducedMotion ? v.score : score;
	const displayDone = reducedMotion || done;

	useEffect(() => {
		const target = v.score;
		if (reducedMotion) {
			return;
		}
		const dur = n * STEP + 120;
		const timers: ReturnType<typeof setTimeout>[] = [];
		for (let k = 0; k < n; k++) {
			timers.push(setTimeout(() => setRevealed(k + 1), (k + 1) * STEP));
		}
		let elapsed = 0;
		const iv = setInterval(() => {
			elapsed += 24;
			const p = Math.min(1, elapsed / dur);
			const eased = 1 - (1 - p) * (1 - p);
			setScore(Math.round(target * eased));
			if (p >= 1) {
				clearInterval(iv);
				setScore(target);
			}
		}, 24);
		const doneT = setTimeout(() => setDone(true), dur + 280);
		return () => {
			timers.forEach(clearTimeout);
			clearInterval(iv);
			clearTimeout(doneT);
		};
		// re-run on tab change AND on re-click of the same tab (run token)
	}, [active, run, n, reducedMotion, v.score]);

	const pick = (i: number) => {
		setRevealed(0);
		setScore(0);
		setDone(false);
		setActive(i);
		setRun((r) => r + 1);
	};

	// Verdict — high score = solid (inverted) fill; else ghost outline. Token-based
	// so it stays high-contrast in both themes.
	const verd =
		v.score >= 85
			? {
					label: t('verdictBlock'),
					sub: 'AUTO-REJECT',
					bg: FG,
					fg: BG,
					dot: BG,
					bd: FG,
					subFaded: true,
				}
			: v.score >= 60
				? {
						label: t('verdictReview'),
						sub: 'MANUAL REVIEW',
						bg: 'transparent',
						fg: FG,
						dot: FG,
						bd: FG,
						subFaded: false,
					}
				: {
						label: t('verdictAllow'),
						sub: 'ALLOW',
						bg: 'transparent',
						fg: MUTED,
						dot: MUTED,
						bd: BORDERH,
						subFaded: false,
					};

	const mono = (size: number, color: string, ls = 1): React.CSSProperties => ({
		fontFamily: MONO,
		fontSize: `${size}px`,
		letterSpacing: `${ls}px`,
		color,
	});

	return (
		<section
			id="antifraud"
			className="relative scroll-mt-36 overflow-hidden py-16 sm:scroll-mt-44 sm:py-24"
		>
			{/* top sweep line */}
			<div
				aria-hidden="true"
				style={{
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					height: '1px',
					background: BORDER,
					overflow: 'hidden',
				}}
			>
				<div
					className="onekyc-sweep"
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '34%',
						height: '1px',
						background: `linear-gradient(90deg,transparent,${FG},transparent)`,
					}}
				/>
			</div>

			{/* section code — matches the other homepage sections (label-mono) */}
			<div className="label-mono">SECTION_03 // ANTI-FRAUD</div>

			{/* heading row */}
			<div
				style={{
					display: 'flex',
					flexWrap: 'wrap',
					justifyContent: 'space-between',
					alignItems: 'flex-end',
					gap: '8px 20px',
					marginTop: '12px',
				}}
			>
				<h2
					className="text-[clamp(30px,4.6vw,52px)] leading-none font-normal tracking-[-0.03em]"
					style={{ margin: 0 }}
				>
					{t('heading')}
				</h2>
				<div
					style={{
						...mono(11, MUTED, 1),
						whiteSpace: 'nowrap',
						paddingBottom: '4px',
					}}
				>
					ENGINE: GRAPH + DEVICE
				</div>
			</div>

			<p
				style={{
					margin: '14px 0 0',
					maxWidth: '64ch',
					fontSize: '14.5px',
					lineHeight: 1.6,
					color: ACCENT,
				}}
			>
				{t('intro')}
			</p>

			<div style={{ height: '1px', background: BORDER, marginTop: '20px' }} />

			{/* grid: tabs + readout — responsive (stacks under md) */}
			<div className="mt-5 grid grid-cols-1 gap-2.5 md:mt-6 md:grid-cols-[248px_1fr] md:gap-3.5">
				{/* LEFT: attack selector */}
				<div className="grid grid-cols-2 gap-2 md:flex md:flex-col">
					{VECTORS.map((vec, i) => {
						const on = i === active;
						return (
							<button
								key={vec.tag}
								type="button"
								onClick={() => pick(i)}
								aria-pressed={on}
								className="cursor-crosshair text-left transition-colors hover:[border-color:var(--color-border-hover)]"
								style={{
									border: `1px solid ${BORDER}`,
									borderLeft: `2px solid ${on ? FG : 'transparent'}`,
									background: on ? SURFACE : 'transparent',
									padding: '13px 14px',
								}}
							>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										...mono(10, MUTED, 1.5),
									}}
								>
									<span>{vec.num}</span>
									<span>{vec.tag}</span>
								</div>
								<div
									style={{
										marginTop: '9px',
										fontSize: '14.5px',
										fontWeight: 500,
										letterSpacing: '-.01em',
										color: on ? FG : MUTED,
									}}
								>
									{vectors[i].name}
								</div>
								<div
									aria-hidden={!on}
									style={{
										marginTop: '8px',
										minHeight: '13px',
										display: 'flex',
										alignItems: 'center',
										gap: '6px',
										visibility: on ? 'visible' : 'hidden',
										...mono(9.5, MUTED, 1.5),
									}}
								>
									<span
										className="onekyc-blink"
										style={{
											width: '5px',
											height: '5px',
											borderRadius: '50%',
											background: FG,
										}}
										aria-hidden="true"
									/>
									{displayDone ? t('statusReady') : t('statusAnalyzing')}
								</div>
							</button>
						);
					})}
				</div>

				{/* RIGHT: live readout */}
				<div
					className="flex flex-col"
					style={{
						border: `1px solid ${BORDER}`,
						padding: '20px 22px 22px',
						position: 'relative',
						minHeight: '382px',
					}}
				>
					{!displayDone && (
						<div
							aria-hidden="true"
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								height: '1px',
								overflow: 'hidden',
							}}
						>
							<div
								className="afx-scan"
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									height: '1px',
									width: '24%',
									background: `linear-gradient(90deg,transparent,${FG},transparent)`,
								}}
							/>
						</div>
					)}

					<div
						style={{
							display: 'flex',
							flexWrap: 'wrap',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: '10px 12px',
						}}
					>
						<div style={mono(11, MUTED, 1)}>
							{t('attemptWord')} {v.id} &nbsp;·&nbsp; {v.tag}
						</div>
						<div
							aria-hidden={!displayDone}
							className={displayDone ? 'afx-in' : undefined}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: '8px',
								minWidth: '214px',
								minHeight: '30px',
								border: `1px solid ${displayDone ? verd.bd : 'transparent'}`,
								background: displayDone ? verd.bg : 'transparent',
								padding: '5px 11px',
								visibility: displayDone ? 'visible' : 'hidden',
							}}
						>
							<span
								style={{
									width: '6px',
									height: '6px',
									borderRadius: '50%',
									background: verd.dot,
								}}
								aria-hidden="true"
							/>
							<span style={{ ...mono(11, verd.fg, 1), fontWeight: 700 }}>
								{verd.label}
							</span>
							<span
								style={{
									...mono(9.5, verd.fg, 1),
									opacity: verd.subFaded ? 0.6 : 0.7,
								}}
							>
								{verd.sub}
							</span>
						</div>
					</div>

					<div
						style={{
							display: 'flex',
							alignItems: 'flex-end',
							gap: '20px',
							marginTop: '18px',
						}}
					>
						<div
							style={{
								...mono(0, FG),
								fontSize: 'clamp(40px,7vw,52px)',
								fontWeight: 700,
								letterSpacing: '-.03em',
								lineHeight: 0.82,
								width: '3ch',
								flex: 'none',
								textAlign: 'right',
								fontVariantNumeric: 'tabular-nums',
							}}
						>
							{displayScore}
						</div>
						<div style={{ flex: 1, paddingBottom: '5px' }}>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									...mono(9.5, MUTED, 1.5),
								}}
							>
								<span>{t('riskScore')}</span>
								<span>0–100</span>
							</div>
							<div
								style={{
									height: '3px',
									background: BORDER,
									marginTop: '8px',
									overflow: 'hidden',
								}}
							>
								<div
									style={{
										height: '100%',
										width: `${Math.max(0, Math.min(100, displayScore))}%`,
										background: FG,
										transition: 'width .1s linear',
									}}
								/>
							</div>
						</div>
					</div>

					<div style={{ marginTop: '18px' }}>
						{v.sigTags.map((tag, k) => {
							const lit = k < displayRevealed;
							const high = v.sev[k] === 'high';
							return (
								<div
									key={tag}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '12px',
										padding: '9px 0',
										borderTop: `1px solid ${SUBTLE}`,
									}}
								>
									<span
										style={{
											width: '7px',
											height: '7px',
											borderRadius: '50%',
											flex: 'none',
											background: lit ? (high ? FG : 'transparent') : BORDERH,
											border:
												lit && !high
													? `1.5px solid ${FG}`
													: '1.5px solid transparent',
										}}
										aria-hidden="true"
									/>
									<span
										style={{
											...mono(10, MUTED, 1),
											width: '92px',
											flex: 'none',
										}}
										className="max-[480px]:!w-[58px]"
									>
										{tag}
									</span>
									<span
										style={{
											flex: 1,
											fontSize: '13px',
											color: lit ? FG : MUTED,
											transition: 'color 120ms linear',
										}}
									>
										{vectors[active].signals[k]}
									</span>
									<span
										aria-hidden={!lit}
										className="max-[480px]:!w-[70px]"
										style={{
											...mono(9.5, high ? FG : MUTED, 1),
											width: '92px',
											flex: 'none',
											textAlign: 'right',
											opacity: lit ? 1 : 0,
											transition: 'opacity 120ms linear',
											visibility: lit ? 'visible' : 'hidden',
										}}
									>
										{t('detected')}
									</span>
								</div>
							);
						})}
					</div>
				</div>
			</div>

			{/* capability chips + learn-more */}
			<div className="mt-[18px] flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
				<div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
					{CHIPS.map((c) => (
						<span
							key={c}
							style={{
								...mono(10, MUTED, 1.2),
								border: `1px solid ${BORDER}`,
								padding: '6px 11px',
							}}
						>
							{c}
						</span>
					))}
				</div>
				<IntlLink
					href="/antifraud"
					className="cursor-crosshair self-start transition-colors hover:[border-color:var(--color-fg)] sm:self-auto"
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '12px',
						border: `1px solid ${BORDER}`,
						padding: '13px 22px',
						color: FG,
						textDecoration: 'none',
						whiteSpace: 'nowrap',
						...mono(11.5, FG, 1.5),
					}}
				>
					<span>{t('learnMore')}</span>
					<span aria-hidden="true">-&gt;</span>
				</IntlLink>
			</div>
		</section>
	);
}
